<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { landingRouteFor } from '@/router/landing';

// Email-only "remember me": we persist the email address (never the password)
// so it can be pre-filled on the next visit. localStorage so it survives a
// browser restart; the session/token still lives in sessionStorage as before.
const REMEMBERED_EMAIL_KEY = 'remembered_email';

const auth = useAuthStore();
const router = useRouter();

const form = reactive({ email: '', password: '' });
const submitting = ref(false);
const showPassword = ref(false);
const rememberMe = ref(false);

onMounted(() => {
  const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
  if (savedEmail) {
    form.email = savedEmail;
    rememberMe.value = true;
  }
});

async function attemptLogin(): Promise<void> {
  if (submitting.value) return;
  submitting.value = true;
  try {
    await auth.login(form.email, form.password);
    if (rememberMe.value) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, form.email);
    } else {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }
    await router.replace(landingRouteFor(auth));
  } catch {
    // auth.authenticationError is already flagged; banner re-renders.
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <form
    class="signin"
    novalidate
    @submit.prevent="attemptLogin"
  >
    <h1 class="signin__title">Welcome to EMSL Analytical, Inc.</h1>

    <p v-if="auth.authenticationError" class="signin__error" role="alert">
      We do not recognize your username/password combo.
    </p>

    <label class="signin__field" for="signin-email">
      <input
        id="signin-email"
        v-model="form.email"
        type="email"
        name="email"
        autocomplete="email"
        placeholder="Email Address"
        required
      />
    </label>

    <label class="signin__field" for="signin-password">
      <div class="signin__password">
        <input
          id="signin-password"
          v-model="form.password"
          :type="showPassword ? 'text' : 'password'"
          name="password"
          autocomplete="current-password"
          placeholder="Password"
          required
        />
        <button
          type="button"
          class="signin__toggle"
          :aria-label="showPassword ? 'Hide password' : 'Show password'"
          :aria-pressed="showPassword"
          @click="showPassword = !showPassword"
        >
          <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" aria-hidden="true" />
        </button>
      </div>
    </label>

    <label class="signin__remember">
      <input v-model="rememberMe" type="checkbox" name="remember" />
      <span>Remember me</span>
    </label>

    <button type="submit" :disabled="submitting" class="signin__submit">
      {{ submitting ? 'Signing in…' : 'Sign In' }}
    </button>

    <p class="signin__links">
      <RouterLink :to="{ name: 'forgotPassword' }">Forgot your password?</RouterLink>
    </p>
  </form>
</template>

<style scoped>
.signin {
  max-width: 24rem;
  margin: 4rem auto;
  padding: 2rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.signin__title {
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  text-align: center;
}
.signin__error {
  margin: 0;
  padding: 0.75rem 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  color: #b91c1c;
}
.signin__field input {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 1rem;
}
.signin__password {
  position: relative;
  display: flex;
  align-items: center;
}
.signin__password input {
  /* room for the toggle button so text doesn't run under it */
  padding-right: 2.75rem;
}
.signin__toggle {
  position: absolute;
  right: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  background: none;
  border: 0;
  color: #6b7280;
  cursor: pointer;
}
.signin__toggle:hover {
  color: #374151;
}
.signin__remember {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
}
.signin__remember input {
  width: 1rem;
  height: 1rem;
  cursor: pointer;
}
.signin__submit {
  padding: 0.625rem 1rem;
  background: #2563eb;
  color: #fff;
  border: 0;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
}
.signin__submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.signin__links {
  margin: 0;
  text-align: center;
  font-size: 0.875rem;
}
</style>
